export const CRM_PARTNER_ROLES = {
  // Поставщик товаров/услуг
  SUPPLIER: 'SUPPLIER',
  // Клиент/заказчик
  CUSTOMER: 'CUSTOMER',
  // Подрядчик/исполнитель работ
  CONTRACTOR: 'CONTRACTOR',
  // Перевозчик/логистический партнёр
  CARRIER: 'CARRIER',
} as const;

export const CRM_PARTNER_STATUSES = {
  /** Активный — есть сделки за последний год */
  ENGAGED: 'ENGAGED',
  /** Потенциальный — нет сделок, но есть касания за год */
  PROSPECT: 'PROSPECT',
  /** Неактивный — нет ни сделок, ни касаний за год */
  DORMANT: 'DORMANT',
  /** Отток (зарезервировано) */
  CHURNED: 'CHURNED',
} as const;

/**
 * Типы accounting-документов 1С-обмена, которыми владеет CRM.
 * Счета/оплаты поставщиков (SUPPLIER_INVOICE / SUPPLIER_PAYMENT) — в модуле procurement.
 */
export const DOCTYPE = {
  SUPPLIER_DOC: 'SUPPLIER_DOC',
  PAY_DOC: 'PAY_DOC',
  SELL_DOC: 'SELL_DOC',
  ORDER_DOC: 'ORDER_DOC',
} as const;

export const CRM_DEAL_TYPES = {
  SALE: 'SALE',
} as const;

export const CRM_DEAL_STATUSES = {
  SALE_REQUEST: 'SALE_REQUEST',
  SALE_IN_PROGRESS: 'SALE_IN_PROGRESS',
  SALE_SHIPPED: 'SALE_SHIPPED',
  SALE_PAID: 'SALE_PAID',
  SALE_CLOSED: 'SALE_CLOSED',
  SALE_CANCELED: 'SALE_CANCELED',
} as const;

export const CRM_LEAD_TYPES = {
  /** @deprecated будет удалён после миграции на ACQUISITION (Шаг Б). */
  BASIC_LEAD: 'BASIC_LEAD',
  /** Входящая заявка */
  INCOMING_REQUEST: 'INCOMING_REQUEST',
  /** Привлечение нового клиента — основной флоу продаж. */
  ACQUISITION: 'ACQUISITION',
  /** Удержание активного клиента — профилактика оттока. */
  RETENTION: 'RETENTION',
  /** Возврат ушедшего клиента — реактивационный флоу. */
  WINBACK: 'WINBACK',
} as const;

export const CRM_LEAD_STATUSES = {
  // ── BASIC_LEAD (legacy, удалится в Шаге Б) ──────────────────────────────
  NEW: 'NEW',
  CONTACTED: 'CONTACTED',
  QUALIFIED: 'QUALIFIED',
  WON: 'WON',
  LOST: 'LOST',
  // ── INCOMING_REQUEST (zombie, тип не регистрируется) ────────────────────
  INCOMING: 'INCOMING',
  ASSIGNED: 'ASSIGNED',
  IN_PROGRESS: 'IN_PROGRESS',
  NO_ORDER: 'NO_ORDER',
  ORDERED: 'ORDERED',
  POSTPONED: 'POSTPONED',
  REJECTED: 'REJECTED',
  // ── ACQUISITION ─────────────────────────────────────────────────────────
  ACQ_NEW: 'ACQ_NEW',
  ACQ_CONTACTED: 'ACQ_CONTACTED',
  ACQ_WON: 'ACQ_WON',
  ACQ_LOST: 'ACQ_LOST',
  // ── RETENTION ───────────────────────────────────────────────────────────
  RET_NEW: 'RET_NEW',
  RET_CONTACTED: 'RET_CONTACTED',
  RET_WON: 'RET_WON',
  RET_LOST: 'RET_LOST',
  // ── WINBACK ─────────────────────────────────────────────────────────────
  WB_NEW: 'WB_NEW',
  WB_CONTACTED: 'WB_CONTACTED',
  WB_WON: 'WB_WON',
  WB_LOST: 'WB_LOST',
} as const;

/**
 * Базовый справочник причин отказа / отмены.
 * Используется при закрытии лида в LOST и при отмене сделки.
 * Регистрируется в crm/server/setup.ts через defineLostReason().
 * Инстансы могут расширять набор своими записями.
 */
export const CRM_LOST_REASONS = {
  NO_NEED: 'NO_NEED',
  TOO_EXPENSIVE: 'TOO_EXPENSIVE',
  CHOSE_COMPETITOR: 'CHOSE_COMPETITOR',
  NO_CONTACT: 'NO_CONTACT',
  NOT_OUR_PROFILE: 'NOT_OUR_PROFILE',
  BUDGET_FROZEN: 'BUDGET_FROZEN',
  OTHER: 'OTHER',
} as const;

/** Справочник источников лидов (каналы входящих заявок) */
export const CRM_LEAD_SOURCES = {
  WEBSITE: 'WEBSITE',
  PHONE: 'PHONE',
  EMAIL: 'EMAIL',
  TELEGRAM: 'TELEGRAM',
  WHATSAPP: 'WHATSAPP',
  REFERRAL: 'REFERRAL',
  EXHIBITION: 'EXHIBITION',
  OTHER: 'OTHER',
} as const;

export const CRM_PARTNER_EXTERNAL_ACCOUNT_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
} as const;

/** Типы групп пользователей, используемые CRM модулем */
export const CRM_GROUPS = {
  /** Филиал — распределение менеджеров по офисам/городам */
  BRANCH: 'BRANCH',
} as const;

// ============================================================================
// Планы продаж
// ============================================================================

/** Типы периодов планирования */
export const CRM_SALES_PLAN_PERIOD_TYPES = ['month', 'quarter', 'year'] as const;
export type SalesPlanPeriodType = (typeof CRM_SALES_PLAN_PERIOD_TYPES)[number];

export const CRM_SALES_PLAN_PERIOD_TYPE_LABELS: Record<SalesPlanPeriodType, string> = {
  month: 'Месяц',
  quarter: 'Квартал',
  year: 'Год',
};

/** Типы планов */
export const CRM_SALES_PLAN_TYPES = ['revenue', 'shipment', 'profit'] as const;
export type SalesPlanType = (typeof CRM_SALES_PLAN_TYPES)[number];

export const CRM_SALES_PLAN_TYPE_LABELS: Record<SalesPlanType, string> = {
  revenue: 'Выручка',
  shipment: 'Отгрузки',
  profit: 'Прибыль',
};

/** Статусы плана продаж */
export const CRM_SALES_PLAN_STATUSES = ['draft', 'approved'] as const;
export type SalesPlanStatus = (typeof CRM_SALES_PLAN_STATUSES)[number];

export const CRM_SALES_PLAN_STATUS_LABELS: Record<SalesPlanStatus, string> = {
  draft: 'Черновик',
  approved: 'Утверждён',
};
